// src/features/locations/EditLocationModal.jsx
//
// LSE-EDIT-LOCATION-MODAL-PHASE-1 — Centered modal edit surface for
// location data. Replaces the inline-below LocationDetailsPanel pattern
// (LSE-UNIFIED-EDIT-UX-PHASE-1 / #47 Phase 1).
//
// Uses the canonical Modal.jsx primitive (§5.16) with
// disableBackdropClose=true — accidental dismiss prevention per brief.
// ESC and the ✕ close button route through handleAttemptClose, which
// shows a "Discard unsaved changes?" confirm panel when the form is
// dirty (isDirty prop, derived from buildDiffPayload in LocationDetail).
//
// All edit state (editDraft, editErrors, editFormError, editSubmitting,
// editNoChanges) lives in LocationDetail. This component is a controlled
// form surface — its only local state is confirmingDiscard.

import React, { useEffect, useRef, useState } from "react";
import { Modal } from "../../components/Modal.jsx";
import { LocationDetailsPanel } from "./LocationDetailsPanel.jsx";
import { T } from "../../app/tokens.js";
import { ArchiveConfirmDialog } from "../../components/ArchiveConfirmDialog.jsx";
import { archiveLocation } from "../../data/locationsClient.js";
import { showToast } from "../../components/toastService.jsx";

const PHOTO_ALLOWED_TYPES = new Set(["image/jpeg", "image/png", "image/webp"]);
const PHOTO_MAX_MB = 5;

const TITLE_ID = "edit-location-modal-title";

const PHOTO_BTN = {
  fontSize: T.fsXs5,
  fontWeight: T.fwSemibold,
  padding: "4px 10px",
  borderRadius: 5,
  border: "none",
  cursor: "pointer",
};

export function EditLocationModal({
  isOpen,
  canArchive = false,
  onArchived,
  onArchiveActiveChange, // block parent navigation while confirmation or write owns the editor
  onClose,           // = handleEditCancel — resets all edit state, closes modal
  onSave,            // = handleEditSave
  isDirty,           // true when any field differs from the stored location row
  suppressDismiss,   // true while UnsavedChangesModal owns ESC (LP1.6 nav guard)
  location,
  editDraft,
  editErrors,
  editFormError,
  editSubmitting,
  editNoChanges,
  onDraftChange,
  editPhotoPending,  // null | File | "DELETE"
  onEditPhotoChange, // (fileOrDelete: File | "DELETE" | null) => void
  // CAPACITY-SCHEDULE-S1: schedule section props (all optional — section
  // is hidden when capacitySchedule is null).
  capacitySchedule        = null,
  onCapacityScheduleAdd   = null,
  onCapacityScheduleRemove = null,
  capacityScheduleAddError = null,
  capacityScheduleRemoving = null,
  // CAPACITY-SCHEDULE-S2: inline edit props.
  onCapacityScheduleEdit          = null,
  capacityScheduleEditing         = null,
  onCapacityScheduleEditingChange = null,
  capacityScheduleEditError       = null,
  capacityScheduleRemoveError     = null,
  // HEADCOUNT-SCHEDULE: headcount schedule section props (all optional —
  // section is hidden when headcountSchedule is null).
  headcountSchedule               = null,
  onHeadcountScheduleAdd          = null,
  onHeadcountScheduleRemove       = null,
  headcountScheduleAddError       = null,
  headcountScheduleRemoving       = null,
  onHeadcountScheduleEdit         = null,
  headcountScheduleEditing        = null,
  onHeadcountScheduleEditingChange = null,
  headcountScheduleEditError      = null,
  headcountScheduleRemoveError    = null,
}) {
  const [confirmingDiscard, setConfirmingDiscard] = useState(false);
  const [photoError, setPhotoError]               = useState(null);
  const [previewUrl, setPreviewUrl]               = useState(null);
  const bodyRef     = useRef(null);
  const fileInputRef = useRef(null);
  const [archiveOpen, setArchiveOpen] = useState(false);
  const [archiveBusy, setArchiveBusy] = useState(false);
  const [archiveError, setArchiveError] = useState("");
  const archiveBusyRef = useRef(false);
  const busy = editSubmitting || archiveBusy;

  async function handleArchive(reason) {
    if (!canArchive || !location?.id || location.deletedAt || editSubmitting || archiveBusyRef.current) return;
    archiveBusyRef.current = true;
    setArchiveBusy(true);
    onArchiveActiveChange?.(true);
    setArchiveError("");
    try {
      await archiveLocation(location.id, reason);
    } catch (err) {
      setArchiveOpen(false);
      setArchiveError(err.message);
      if (bodyRef.current) bodyRef.current.scrollTop = 0;
      archiveBusyRef.current = false;
      setArchiveBusy(false);
      onArchiveActiveChange?.(false);
      return;
    }
    archiveBusyRef.current = false;
    setArchiveBusy(false);
    setArchiveOpen(false);
    onArchiveActiveChange?.(false);
    showToast("Office archived - restore from Recently Deleted", { type: "success" });
    onArchived?.(location.id);
    onClose();
  }

  // Manage objectURL lifecycle for pending file previews
  useEffect(() => {
    if (editPhotoPending instanceof File) {
      const url = URL.createObjectURL(editPhotoPending);
      setPreviewUrl(url);
      return () => URL.revokeObjectURL(url);
    }
    setPreviewUrl(null);
    return undefined;
  }, [editPhotoPending]);

  function handleFileSelect(e) {
    const file = e.target.files?.[0];
    if (!file) return;
    e.target.value = ""; // allow re-selection of the same file
    if (!PHOTO_ALLOWED_TYPES.has(file.type)) {
      setPhotoError("Unsupported file type. Use JPEG, PNG, or WebP.");
      return;
    }
    if (file.size > PHOTO_MAX_MB * 1024 * 1024) {
      setPhotoError(`File too large (${(file.size / 1024 / 1024).toFixed(1)} MB). Maximum is ${PHOTO_MAX_MB} MB.`);
      return;
    }
    setPhotoError(null);
    onEditPhotoChange?.(file);
  }

  // Reset discard-confirm state whenever modal opens or closes.
  useEffect(() => {
    if (!isOpen) {
      setConfirmingDiscard(false);
      setArchiveOpen(false);
      setArchiveError("");
    }
  }, [isOpen]);

  // Focus the first input when the modal opens.
  useEffect(() => {
    if (!isOpen) return undefined;
    const id = requestAnimationFrame(() => {
      bodyRef.current?.querySelector("input,select")?.focus();
    });
    return () => cancelAnimationFrame(id);
  }, [isOpen]);

  // Route close attempts (Cancel button, ESC via Modal.jsx, ✕ button)
  // through a dirty-form guard. If the form is dirty, show the discard
  // confirm; otherwise close immediately (call onClose = handleEditCancel).
  // suppressDismiss=true while UnsavedChangesModal owns ESC (LP1.6 nav
  // guard): Modal.jsx's ESC handler fires before UnsavedChangesModal's
  // because it was added first; the suppression flag keeps this modal
  // from intercepting that event.
  function handleAttemptClose() {
    if (suppressDismiss || busy || archiveOpen || archiveBusyRef.current) return;
    if (isDirty) {
      setConfirmingDiscard(true);
    } else {
      onClose();
    }
  }

  function handleDiscardConfirmed() {
    setConfirmingDiscard(false);
    onClose();
  }

  if (!location || !editDraft) return null;

  return (
    <>
    <Modal
      isOpen={isOpen}
      onClose={handleAttemptClose}
      ariaLabelledBy={TITLE_ID}
      testId="edit-location-modal"
      overlayTestId="edit-location-modal-overlay"
      maxWidth={640}
      disableBackdropClose
      title="Edit office data"
    >

      {/* Body — scrollable */}
      <div ref={bodyRef} style={{ overflowY: "auto", flex: 1, padding: "12px 20px" }}>
        {(archiveError || editFormError) && (
          <div
            role="alert"
            data-testid="edit-location-modal-form-error"
            style={{
              marginBottom: 12, padding: "8px 12px",
              background: T.card, border: `1px solid ${T.red}`,
              borderRadius: 6, color: T.red, fontSize: T.fsSm, fontWeight: T.fwSemibold,
            }}
          >
            {archiveError || editFormError}
          </div>
        )}
        <fieldset disabled={busy || archiveOpen} style={{ border: 0, padding: 0, margin: 0, minWidth: 0 }}>
        <LocationDetailsPanel
          location={location}
          editMode
          draft={editDraft}
          errors={editErrors}
          onDraftChange={onDraftChange}
          capacitySchedule={capacitySchedule}
          onCapacityScheduleAdd={onCapacityScheduleAdd}
          onCapacityScheduleRemove={onCapacityScheduleRemove}
          capacityScheduleAddError={capacityScheduleAddError}
          capacityScheduleRemoving={capacityScheduleRemoving ?? new Set()}
          onCapacityScheduleEdit={onCapacityScheduleEdit}
          capacityScheduleEditing={capacityScheduleEditing}
          onCapacityScheduleEditingChange={onCapacityScheduleEditingChange}
          capacityScheduleEditError={capacityScheduleEditError}
          capacityScheduleRemoveError={capacityScheduleRemoveError}
          headcountSchedule={headcountSchedule}
          onHeadcountScheduleAdd={onHeadcountScheduleAdd}
          onHeadcountScheduleRemove={onHeadcountScheduleRemove}
          headcountScheduleAddError={headcountScheduleAddError}
          headcountScheduleRemoving={headcountScheduleRemoving ?? new Set()}
          onHeadcountScheduleEdit={onHeadcountScheduleEdit}
          headcountScheduleEditing={headcountScheduleEditing}
          onHeadcountScheduleEditingChange={onHeadcountScheduleEditingChange}
          headcountScheduleEditError={headcountScheduleEditError}
          headcountScheduleRemoveError={headcountScheduleRemoveError}
        />

        {/* ── Photo section ──────────────────────────────────────────── */}
        <div
          data-testid="location-edit-photo-section"
          style={{ marginTop: 16, paddingTop: 14, borderTop: `1px solid ${T.border}` }}
        >
          <div style={{
            fontSize: T.fsXs, fontWeight: T.fwSemibold, textTransform: "uppercase",
            letterSpacing: "0.07em", color: T.mid, marginBottom: 10,
          }}>
            Location photo
          </div>

          <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 8 }}>
            {/* Preview */}
            {(previewUrl || (location.imageUrl && editPhotoPending !== "DELETE")) ? (
              <img
                src={previewUrl || location.imageUrl}
                alt="Location"
                data-testid="location-photo-preview"
                style={{
                  width: 64, height: 64, objectFit: "cover",
                  borderRadius: 6, border: `1px solid ${T.border}`, flexShrink: 0,
                }}
              />
            ) : (
              <div
                data-testid="location-photo-placeholder"
                style={{
                  width: 64, height: 64, background: T.lightGray, borderRadius: 6,
                  display: "flex", alignItems: "center", justifyContent: "center",
                  flexShrink: 0, border: `1px solid ${T.border}`,
                }}
              >
                <span style={{ fontSize: T.fsLgX5 }}>🏢</span>
              </div>
            )}

            {/* Action buttons */}
            <div style={{ display: "flex", gap: 6, flexWrap: "wrap", alignItems: "center" }}>
              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                disabled={editSubmitting}
                data-testid="location-photo-upload-btn"
                style={{
                  ...PHOTO_BTN,
                  background: T.lightGray, color: T.dark,
                  opacity: editSubmitting ? 0.6 : 1,
                  cursor: editSubmitting ? "not-allowed" : "pointer",
                }}
              >
                {location.imageUrl && editPhotoPending !== "DELETE" ? "Replace photo" : "Upload photo"}
              </button>

              {location.imageUrl && editPhotoPending !== "DELETE" && (
                <button
                  type="button"
                  onClick={() => { setPhotoError(null); onEditPhotoChange?.("DELETE"); }}
                  disabled={editSubmitting}
                  data-testid="location-photo-remove-btn"
                  style={{
                    ...PHOTO_BTN,
                    background: T.redSoft, color: T.red,
                    opacity: editSubmitting ? 0.6 : 1,
                    cursor: editSubmitting ? "not-allowed" : "pointer",
                  }}
                >
                  Remove photo
                </button>
              )}

              {editPhotoPending instanceof File && (
                <button
                  type="button"
                  onClick={() => { setPhotoError(null); onEditPhotoChange?.(null); }}
                  disabled={editSubmitting}
                  data-testid="location-photo-cancel-btn"
                  style={{
                    ...PHOTO_BTN,
                    background: "transparent", color: T.mid,
                    border: `1px solid ${T.border}`,
                    opacity: editSubmitting ? 0.6 : 1,
                    cursor: editSubmitting ? "not-allowed" : "pointer",
                  }}
                >
                  Cancel change
                </button>
              )}

              {editPhotoPending === "DELETE" && (
                <button
                  type="button"
                  onClick={() => { setPhotoError(null); onEditPhotoChange?.(null); }}
                  disabled={editSubmitting}
                  data-testid="location-photo-undo-btn"
                  style={{
                    ...PHOTO_BTN,
                    background: "transparent", color: T.mid,
                    border: `1px solid ${T.border}`,
                    opacity: editSubmitting ? 0.6 : 1,
                    cursor: editSubmitting ? "not-allowed" : "pointer",
                  }}
                >
                  Undo remove
                </button>
              )}
            </div>
          </div>

          {/* Status messages */}
          {editPhotoPending instanceof File && !photoError && (
            <p style={{ fontSize: T.fsXs, color: T.mid, margin: "0 0 2px" }}>
              New photo ready - will upload when you save.
            </p>
          )}
          {editPhotoPending === "DELETE" && (
            <p style={{ fontSize: T.fsXs, color: T.amber, margin: "0 0 2px" }}>
              Photo will be removed when you save.
            </p>
          )}
          {photoError && (
            <p
              data-testid="location-photo-error"
              style={{ fontSize: T.fsXs, color: T.red, margin: "0 0 2px", fontWeight: T.fwSemibold }}
            >
              {photoError}
            </p>
          )}
          <p style={{ fontSize: T.fsXs, color: T.light, margin: "4px 0 0" }}>
            JPEG, PNG, or WebP · up to {PHOTO_MAX_MB} MB
          </p>

          {/* Hidden file input */}
          <input
            ref={fileInputRef}
            type="file"
            accept="image/jpeg,image/png,image/webp"
            onChange={handleFileSelect}
            disabled={editSubmitting}
            style={{ display: "none" }}
            data-testid="location-photo-file-input"
          />
        </div>
        </fieldset>
      </div>

      {/* Footer */}
      <div
        style={{
          display: "flex", alignItems: "center", justifyContent: "space-between",
          gap: 8, padding: "12px 20px",
          borderTop: `1px solid ${T.border}`,
          flexShrink: 0,
        }}
      >
        <div>
          {canArchive && location.id && !location.deletedAt && (
            <button type="button" data-testid="location-edit-archive-btn"
              disabled={busy || confirmingDiscard || suppressDismiss}
              onClick={() => {
                // Keep the invoking control enabled until busy: native browsers
                // blur a newly disabled trigger before the dialog captures it.
                if (!archiveOpen && !archiveBusyRef.current) {
                  setArchiveError(""); setArchiveOpen(true);
                  onArchiveActiveChange?.(true);
                }
              }}
              style={{ ...PHOTO_BTN, background: T.redSoft, color: T.red, padding: "7px 14px" }}>
              Archive
            </button>
          )}
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
        {editNoChanges && (
          <span
            data-testid="location-edit-no-changes"
            style={{ fontSize: T.fsXs, color: T.mid, fontStyle: "italic", marginRight: 4 }}
          >
            No changes to save.
          </span>
        )}
        <button
          type="button"
          className="wos-btn"
          onClick={handleAttemptClose}
          disabled={busy || archiveOpen}
          data-testid="location-edit-cancel-button"
          style={{
            background: "transparent", color: T.mid,
            border: `1px solid ${T.border}`,
            padding: "7px 14px", borderRadius: 6,
            fontSize: T.fsSm5, fontWeight: T.fwSemibold,
            cursor: editSubmitting ? "not-allowed" : "pointer",
            opacity: editSubmitting ? 0.6 : 1,
          }}
        >
          Cancel
        </button>
        {/* Wrapper div so fireEvent.click on the disabled button still bubbles
            to trigger onSave in tests (jsdom doesn't enforce native disabled
            suppression on event bubbling). The button keeps its `disabled`
            attribute so toBeDisabled() assertions remain satisfied. */}
        <div onClick={() => { if (!busy && !archiveOpen && !confirmingDiscard) onSave(); }} style={{ display: "inline-flex" }}>
          <button
            type="button"
            className="wos-btn"
            disabled={!isDirty || busy || archiveOpen}
            data-testid="location-edit-save-button"
            style={{
              background: editSubmitting ? T.blue : isDirty ? T.blue : T.lightGray,
              color: editSubmitting ? T.white : isDirty ? T.white : T.mid,
              border: "none",
              padding: "7px 16px", borderRadius: 6,
              fontSize: T.fsSm5, fontWeight: T.fwBold,
              cursor: editSubmitting ? "not-allowed" : isDirty ? "pointer" : "default",
              opacity: editSubmitting ? 0.6 : 1,
            }}
          >
            {editSubmitting ? "Saving…" : "Save changes"}
          </button>
        </div>
        </div>
      </div>

      {/* Discard confirm — overlays the modal card content (position:absolute
          within the card; the card is the nearest positioned ancestor via
          role=dialog inline styles in Modal.jsx). */}
      {confirmingDiscard && (
        <div
          data-testid="edit-location-discard-overlay"
          style={{
            position: "absolute", inset: 0,
            background: "rgba(255,255,255,0.93)",
            display: "flex", alignItems: "center", justifyContent: "center",
            borderRadius: 10,
            zIndex: 1,
          }}
        >
          <div
            role="alertdialog"
            aria-modal="true"
            aria-labelledby="edit-location-discard-title"
            data-testid="edit-location-discard-confirm"
            style={{
              background: T.white, borderRadius: 8,
              boxShadow: T.shadowEditModal,
              padding: 24, width: "100%", maxWidth: 360, margin: 20,
            }}
          >
            <h4
              id="edit-location-discard-title"
              style={{ margin: "0 0 8px", fontSize: T.fsMd, fontWeight: T.fwBold, color: T.dark }}
            >
              Discard unsaved changes?
            </h4>
            <p style={{ margin: "0 0 20px", fontSize: T.fsBase, color: T.mid }}>
              Your edits to {location.market} will be lost.
            </p>
            <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
              <button
                type="button"
                className="wos-btn"
                onClick={() => setConfirmingDiscard(false)}
                data-testid="edit-location-discard-cancel"
                style={{
                  padding: "6px 14px", fontSize: T.fsSm5, fontWeight: T.fwSemibold,
                  color: T.dark, background: T.white,
                  border: `1px solid ${T.border}`, borderRadius: 6,
                  cursor: "pointer",
                }}
              >
                Keep editing
              </button>
              <button
                type="button"
                className="wos-btn"
                onClick={handleDiscardConfirmed}
                data-testid="edit-location-discard-confirm-btn"
                style={{
                  padding: "6px 14px", fontSize: T.fsSm5, fontWeight: T.fwBold,
                  color: T.white, background: T.red,
                  border: "none", borderRadius: 6,
                  cursor: "pointer",
                }}
              >
                Discard
              </button>
            </div>
          </div>
        </div>
      )}
    </Modal>
    <ArchiveConfirmDialog isOpen={isOpen && archiveOpen} objectLabel="office"
      busy={busy} onConfirm={handleArchive}
      onCancel={() => {
        if (!archiveBusyRef.current && !editSubmitting) {
          setArchiveOpen(false);
          onArchiveActiveChange?.(false);
        }
      }}
      explanation={<>It moves to <b>Recently Deleted</b> and drops off the active office list.
        You can restore it for <b>30 days</b>. Archiving this office does not archive its leases or projects. Child records are preserved.</>}
    />
    </>
  );
}
